import './LocationsTables.scss';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import {
  Button,
  GridItem,
  Icons,
  PendingContent,
  Spinner,
  TableV2,
  Tabs,
} from 'plume-ui';
import { ColumnType } from 'plume-ui/dist/components/TableV2/types';

import ErrorBlock from '../../../../components/ErrorBlock/ErrorBlock';
import { getFrontlineUrl } from '../../../../utils/environments';
import { AutonomousMonitorLocation } from '../../types';
import InfoCard from './InfoCard';
import { STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { Tab } from 'plume-ui/dist/components/Tabs/Tabs';

interface LocationsTablesProps {
  outlierLocations: AutonomousMonitorLocation[];
  highNormalLocations: AutonomousMonitorLocation[];
  loading?: boolean;
  error?: boolean;
}

const LocationsTables = ({
  outlierLocations,
  highNormalLocations,
  loading,
  error,
}: LocationsTablesProps) => {
  const { t } = useTranslation();

  const [downloaded, setDownloaded] = useState(false);

  const [highNormalPage, setHighNormalPage] = useState(0);
  const [outlierPage, setOutlierPage] = useState(0);

  const [selectedTab, setSelectedTab] = useState<'outlier' | 'highNormal'>(
    'outlier',
  );

  const TABLE_COLUMNS: ColumnType<AutonomousMonitorLocation>[] = useMemo(
    () => [
      {
        title: t('monitors.autonomous.monitorDetails.tableColumns.locationId'),
        key: 'location_id',
        dataIndex: 'location_id',
      },
      {
        title: t('monitors.autonomous.monitorDetails.tableColumns.accountId'),
        key: 'account_id',
        dataIndex: 'account_id',
      },
      {
        title: t('monitors.autonomous.monitorDetails.tableColumns.healthScore'),
        key: 'health_score',
        dataIndex: 'health_score',
        render: (row) => row.health_score.toFixed(0),
      },
      {
        title: t('monitors.autonomous.monitorDetails.tableColumns.action'),
        key: 'action',
        render: (row) => (
          <Button
            onClick={() =>
              window.open(
                `${getFrontlineUrl()}/location/${row.location_id}`,
                '_blank',
              )
            }
            styleVariant="tertiary"
          >
            {t('frontline')}
          </Button>
        ),
      },
    ],
    [],
  );

  const onDownload = () => {
    const csvData = outlierLocations.map((location) => ({
      location_id: location.location_id,
      account_id: location.account_id,
      health_score: location.health_score,
    }));

    const csvContent = [
      [
        t('monitors.autonomous.monitorDetails.tableColumns.locationId'),
        t('monitors.autonomous.monitorDetails.tableColumns.accountId'),
        t('monitors.autonomous.monitorDetails.tableColumns.healthScore'),
      ],
      ...csvData.map((row) => [
        row.location_id,
        row.account_id,
        row.health_score,
      ]),
    ];

    const csvContentString = csvContent.map((row) => row.join(',')).join('\n');

    const blob = new Blob([csvContentString], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dayjs().format(
      STANDARD_DATE_TIME_FORMAT,
    )}_outlier_locations.csv`;
    a.click();
    setDownloaded(true);
    setTimeout(() => {
      URL.revokeObjectURL(url);
      setDownloaded(false);
    }, 2000);
  };

  const tabs: Tab[] = [
    {
      id: 'outlier',
      label: t('monitors.autonomous.monitorDetails.outlierLocations'),
      content: null,
    },
    {
      id: 'highNormal',
      label: t('monitors.autonomous.monitorDetails.highNormalLocations'),
      content: null,
    },
  ];

  const showingRange = useMemo(() => {
    if (selectedTab === 'outlier') {
      return `${Math.min(
        outlierPage * 100 + 1,
        outlierLocations.length,
      )}-${Math.min(outlierLocations.length, (outlierPage + 1) * 100)}`;
    }
    return `${Math.min(
      highNormalPage * 100 + 1,
      highNormalLocations.length,
    )}-${Math.min(highNormalLocations.length, (highNormalPage + 1) * 100)}`;
  }, [
    highNormalPage,
    highNormalLocations,
    outlierPage,
    outlierLocations,
    selectedTab,
  ]);

  const totalLocations =
    selectedTab === 'outlier'
      ? outlierLocations.length
      : highNormalLocations.length;

  return (
    <GridItem colSpan="12" tabletColSpan="12">
      <span className="LocationsTables">
        <PendingContent loading={loading || false} loader={Spinner}>
          <InfoCard
            label={
              <Tabs
                tabType="flatTabs"
                options={tabs}
                onSelectTab={(tab) => {
                  setOutlierPage(0);
                  setHighNormalPage(0);
                  setSelectedTab(tab.id as 'outlier' | 'highNormal');
                }}
              />
            }
            optionalTitle={
              error
                ? undefined
                : t('monitors.monitorInfo.unresolvedLocations.showingOfTotal', {
                    showing: showingRange,
                    total: totalLocations,
                  })
            }
            headerRightContent={
              !error &&
              selectedTab === 'outlier' && (
                <Button
                  disabled={outlierLocations.length === 0 || downloaded}
                  classes={(styles) => ({
                    ...styles,
                    root: `${styles.root} LocationsTables__downloadButton`,
                  })}
                  icon={<Icons.DownloadIcon />}
                  styleVariant="tertiary"
                  onClick={onDownload}
                >
                  {t('download')}
                </Button>
              )
            }
          >
            <div className="LocationsTables__table">
              {!error && !loading && (
                <TableV2
                  stickyHeader
                  virtual
                  copyTooltipLabel={t('copied')}
                  columns={TABLE_COLUMNS}
                  data={
                    selectedTab === 'outlier'
                      ? outlierLocations
                      : highNormalLocations
                  }
                  rowKey={(row) => row.location_id}
                  onPaginationChange={(pagination) => {
                    if (selectedTab === 'outlier')
                      setOutlierPage(pagination.page || 0);
                    else setHighNormalPage(pagination.page || 0);
                  }}
                  pagination={{
                    pageSize: 100,
                  }}
                  key={selectedTab}
                />
              )}
              {error && (
                <ErrorBlock height={240} message={t('genericErrorMessage')} />
              )}
            </div>
          </InfoCard>
        </PendingContent>
      </span>
    </GridItem>
  );
};

export default LocationsTables;
